import React from 'react';
import { Box, Typography, Link, Alert, Button } from '@mui/material';
import { Task } from '../../types/task';

interface DocumentContentProps {
    task: Task;
}

export const DocumentContent: React.FC<DocumentContentProps> = ({ task }) => {
    const metadata = task.metadata || {};
    const content = task.content || (task.document_type?.includes('earnings') ? '' : 'No content available');

    // CHART IMAGE: Show image if available
    if (task.document_type === 'chart_data_extraction') {
        if (metadata.image_url) {
            return (
                <Box>
                    <Typography variant="h6">Chart Image:</Typography>
                    <img src={metadata.image_url} alt="Chart" style={{ maxWidth: '100%', maxHeight: 400, borderRadius: 8, marginBottom: 16 }} />
                </Box>
            );
        }
        if (metadata.file_upload) {
            return (
                <Box>
                    <Typography variant="h6">Chart Image:</Typography>
                    <img src={`data:image/*;base64,${metadata.file_upload}`} alt="Chart" style={{ maxWidth: '100%', maxHeight: 400, borderRadius: 8, marginBottom: 16 }} />
                </Box>
            );
        }
        // If no image, show fallback
        return <Typography>No chart image available</Typography>;
    }

    if (content.startsWith('http://') || content.startsWith('https://')) {
        return (
            <>
                <Typography variant="h6">Document Link:</Typography>
                <Link href={content} target="_blank" rel="noopener">
                    Open document in new tab
                </Link>
            </>
        );
    }

    if (task.document_type === 'credit_card' && metadata.transaction) {
        return (
            <>
                <Typography variant="h6">Transaction Details:</Typography>
                <Box component="pre" sx={{ p: 2, bgcolor: 'background.paper', color: 'text.primary', borderRadius: 1 }}>
                    {metadata.transaction}
                </Box>
            </>
        );
    }

    if (task.document_type === 'earnings_search') {
        return (
            <>
                <Typography variant="h6">Earnings Search Details:</Typography>
                <Box sx={{ p: 2, bgcolor: 'background.paper', color: 'text.primary', borderRadius: 1 }}>
                    <Typography><strong>Company:</strong> {metadata.company || 'Not specified'}</Typography>
                    {metadata.quarter && <Typography><strong>Quarter:</strong> {metadata.quarter}</Typography>}
                    {metadata.year && <Typography><strong>Year:</strong> {metadata.year}</Typography>}
                </Box>
            </>
        );
    }

    if (task.document_type === 'text_classification') {
        return (
            <>
                <Typography variant="h6">Email text: </Typography>
                <Box sx={{ p: 2, bgcolor: 'background.paper', color: 'text.primary', borderRadius: 1 }}>
                    {metadata.content && <Typography> {metadata.content}</Typography>}
                </Box>
            </>
        );
    }

    if (task.document_type === 'website') {
        const websiteUrl = task.original_request?.metadata?.url || metadata.url;
        if (websiteUrl) {
            return (
                <>
                    <Alert severity="warning" sx={{ mb: 1 }}>
                        Website content cannot be embedded directly. Please use the link below to view the website:
                    </Alert>
                    <Link href={websiteUrl} target="_blank" rel="noopener">
                        Open Website in New Tab
                    </Link>
                </>
            );
        }
        return <Typography>No website URL available</Typography>;
    }

        // Utility to check if a URL is valid and not a fallback value
    const isValidSourceUrl = (url: string | null | undefined): boolean => {
        if (!url) return false;
        const lower = url.trim().toLowerCase();
        const invalids = [
            '', 'none', 'null', 'no content available', 'no source url available', 'no document source available'
        ];
        return !invalids.includes(lower);
    };

    if ([
        'pdf',
        'sec',
        'revenue_segmentation_table_extraction',
        'lease',
    ].includes(task.document_type)) {
        const docUrl = task.original_request?.metadata?.url || metadata.url || metadata.filing_url || content;
        if (isValidSourceUrl(docUrl)) {
            if (task.document_type === 'sec') {
                return (
                    <>
                        <Alert severity="warning" sx={{ mb: 1 }}>
                            SEC documents cannot be embedded directly. Please use the link below to view the filing:
                        </Alert>
                        <Link href={docUrl} target="_blank" rel="noopener">
                            Open SEC Filing in New Tab
                        </Link>
                    </>
                );
            }
            return (
                <>
                    <Button href={docUrl} target="_blank" variant="contained" color="primary" sx={{ mb: 1 }}>
                        Open in new tab
                    </Button>
                    <Box component="iframe" src={docUrl} sx={{ width: '100%', height: 600 }} />
                </>
            );
        } else {
            // No source URL available
            if (task.document_type === 'sec') {
                return (
                    <Typography sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        No source URL available
                    </Typography>
                );
            } else {
                return (
                    <Typography sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        No document source available
                    </Typography>
                );
            }
        }
    }

    return (
        <Box component="pre" sx={{ p: 2, bgcolor: 'background.paper', color: 'text.primary', borderRadius: 1 }}>
            {content}
        </Box>
    );
}
